import { errorText, saveFile } from '@/api/client';
/**
 * Ferieliste (#114): per person the year's ferie, the main holiday against the right in ferieloven
 * § 7 (1) (18 virkedager in 1 June to 30 September), and the holidays fixed less than two months
 * before they start (§ 6 (2)). The server counts (GET /admin/ferieliste); this shows it, filters it
 * by the page's search and team, and downloads the CSV. It warns, it refuses nothing, and it does
 * not touch a balance or pay.
 */
import { type ApiUser, type FerielisteRow, useFerieliste } from '@/api/data';
import { T } from '@/i18n';
import { Button, Empty, toast } from '@/ui';
import { num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Status } from '@/ui/page';
import { Link } from 'react-router-dom';
import { periodText } from './dates';
import { type Filter, personMatches, teamsOf } from './filters';
import { FilterBar, Person, YearSelect } from './parts';

const dager = (n: number) => `${num(n, 1)} ${n === 1 ? T('dag') : T('dager')}`;

/**
 * The person's line under Merknad: why the main holiday is short, or nothing. «Ikke fastsatt» is
 * the common case and is said once above the table, its figure amber (#272 B-20, V-04).
 */
export function warningText(r: FerielisteRow): string | null {
  if (r.warning === 'declined')
    return T('Søknad i hovedferien avslått, {n} dager mangler av retten.', { n: r.main_missing });
  return null;
}

export function Ferieliste({
  users,
  filter,
  onFilter,
  filtersOpen,
  onFiltersOpen,
  teams,
  today,
  tenantId,
  year,
  onYear,
}: {
  users: ApiUser[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  filtersOpen: boolean;
  onFiltersOpen: (open: boolean) => void;
  teams: string[];
  today: string;
  tenantId: number | undefined;
  /** the page's holiday year (parts.tsx YearSelect) */
  year: number;
  onYear: (year: number) => void;
}) {
  const q = useFerieliste(year);
  const byId = new Map(users.map((u) => [u.id, u]));
  const rows = (q.data?.rows ?? []).filter((r) => {
    const u = byId.get(r.user_id);
    return !!u && personMatches(u, filter, tenantId);
  });
  const missing = rows.filter((r) => r.warning).length;
  const notPlanned = rows.some((r) => r.warning === 'not_planned');
  const short = rows.reduce((n, r) => n + r.short_notice, 0);

  const download = async () => {
    try {
      await saveFile(`/admin/ferieliste.csv?year=${year}`, `ferieliste-${year}.csv`);
      toast(T('CSV lastet ned'));
    } catch (e) {
      toast(errorText(e));
    }
  };

  return (
    <>
      <FilterBar
        value={filter}
        onChange={onFilter}
        teams={teams}
        placeholder={T('Søk ansatt eller team')}
        open={filtersOpen}
        onOpen={onFiltersOpen}
      >
        <YearSelect value={year} onChange={onYear} today={today} />
        <Button onClick={download}>
          <Icon name="download" />
          {T('Last ned CSV')}
        </Button>
      </FilterBar>
      <div className="kpi fkpi" data-testid="ferieliste-cards">
        <div className={missing ? 'w' : ''}>
          <span className="kic tn-warn">
            <Icon name="sun" />
          </span>
          <span>
            {T('Mangler hovedferie')}
            <b>
              {missing} {T('av')} {rows.length}
            </b>
            <i>{T('under tre uker innvilget 1. juni til 30. september')}</i>
          </span>
        </div>
        <div className={short ? 'w' : ''}>
          <span className="kic tn-blue">
            <Icon name="calendar" />
          </span>
          <span>
            {T('Kort varsel')}
            <b>{short}</b>
            <i>{T('ferier fastsatt under to måneder før start')}</i>
          </span>
        </div>
      </div>
      <section className="mcard">
        <div className="mch">
          <h2>{T('Ferieliste {year}', { year })}</h2>
          {notPlanned && (
            <span className="sub" data-testid="ferieliste-note">
              {T('Gul hovedferie er ikke fastsatt, og dagene mangler av retten.')}
            </span>
          )}
        </div>
        {rows.length ? (
          <table className="mt cards">
            <thead>
              <tr>
                <th>{T('Ansatt')}</th>
                <th>{T('Ferie')}</th>
                <th className="num">{T('Dager')}</th>
                <th>{T('Hovedferie')}</th>
                <th>{T('Merknad')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const u = byId.get(r.user_id);
                const w = warningText(r);
                return (
                  <tr key={r.user_id} data-testid="ferieliste-row">
                    <td>
                      <Link to={`/ansatte/${r.user_id}`} className="plain">
                        <Person
                          id={r.user_id}
                          name={r.name}
                          sub={
                            (u &&
                              teamsOf(u, tenantId)
                                .map((t) => t.name)
                                .join(', ')) ||
                            undefined
                          }
                        />
                      </Link>
                    </td>
                    <td data-k={T('Ferie')}>
                      {r.absences.length
                        ? r.absences.map((a) => (
                            <div key={a.id} className="nw">
                              {periodText(a.from, a.to, false)}
                              <small>
                                {' '}
                                {dager(a.days)}
                                {a.status === 'pending' ? `, ${T('søknad')}` : ''}
                                {a.short_notice
                                  ? `, ${T('fastsatt {n} dager før', { n: a.notice_days ?? 0 })}`
                                  : ''}
                              </small>
                            </div>
                          ))
                        : T('Ingen ferie')}
                    </td>
                    <td className="num" data-k={T('Dager')}>
                      {num(r.approved_days, 1)}
                      {r.pending_days ? <small> +{num(r.pending_days, 1)}</small> : null}
                    </td>
                    <td className={`nw${r.warning ? ' fwarn' : ''}`} data-k={T('Hovedferie')}>
                      {r.main_required ? (
                        <>
                          {T('{n} av {m}', { n: num(r.main_days, 1), m: r.main_required })}
                          {r.main_pending_days ? <small> +{num(r.main_pending_days, 1)}</small> : null}
                        </>
                      ) : (
                        <small>{T('Ingen rett i år')}</small>
                      )}
                    </td>
                    <td data-k={T('Merknad')}>
                      {w ? (
                        <Status tone="warn">{w}</Status>
                      ) : r.short_notice ? (
                        <Status tone="warn">{T('Kort varsel')}</Status>
                      ) : (
                        '–'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty>{q.isPending ? T('Laster …') : T('Ingen ansatte passer filteret.')}</Empty>
        )}
      </section>
      <p className="mfoot" style={{ maxWidth: '72ch' }}>
        {T(
          'Ferieloven § 7: den ansatte kan kreve 18 virkedager hovedferie mellom 1. juni og 30. september (15 der bedriften teller arbeidsdager), ikke den som begynner etter 15. august. Hovedferie teller innvilget ferie i perioden; tallet etter + er søknader. Fra 1. april vises den som mangler. Ferieloven § 6: den ansatte kan kreve å få vite om ferien senest to måneder før; kort varsel er greit når den ansatte selv ba om datoene. Feriepenger regnes ikke her.',
        )}
      </p>
    </>
  );
}
